import { format, addDays, parseISO, isAfter, isBefore, startOfDay, isValid } from 'date-fns';
import { enUS, he } from 'date-fns/locale/index.js';

/**
 * Get the schedule for a specific day from the flexible configuration
 * @param {Object} timeSlotConfig - Time slot configuration
 * @param {Date} date - Date object
 * @returns {Object|null} Day schedule or null if closed
 */
const getDaySchedule = (timeSlotConfig, date) => {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  const daySchedules = timeSlotConfig.daySchedules || {};

  // Map day numbers to day names
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayOfWeek];

  // First check for individual day schedule by name
  if (daySchedules[dayName]) {
    return daySchedules[dayName];
  }

  // Then check for numeric day override (0-6)
  if (daySchedules[dayOfWeek]) {
    return daySchedules[dayOfWeek];
  }

  // Backward compatibility: Check for "weekdays" grouping (Mon-Thu)
  if (dayOfWeek >= 1 && dayOfWeek <= 4 && daySchedules.weekdays) {
    return daySchedules.weekdays;
  }

  // Return default if no specific schedule found
  return {
    enabled: true,
    startTime: '0900',
    endTime: '1700',
    slotDuration: timeSlotConfig.defaultSlotDuration || 60,
    slotCapacity: timeSlotConfig.defaultCapacity || 20
  };
};

/**
 * Parse time string to minutes
 * @param {string} timeStr - Time in "HHMM" or "HH:MM" format
 * @returns {number} Minutes since midnight
 */
const parseTimeToMinutes = (timeStr) => {
  const cleanTime = timeStr.replace(':', '');
  const hours = parseInt(cleanTime.slice(0, 2));
  const minutes = parseInt(cleanTime.slice(2, 4)) || 0;
  return hours * 60 + minutes;
};

/**
 * Format minutes to time string
 * @param {number} minutes - Minutes since midnight
 * @returns {string} Time in "HH:MM" format
 */
const formatMinutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Generate time slots for a specific date based on event schedule configuration
 * @param {Object} event - Event object with scheduleConfig or timeSlotConfig
 * @param {string} selectedDate - Date in YYYY-MM-DD format
 * @param {Object} [options] - Generation options
 * @param {boolean} [options.includePastSlots=false] - When true, keep slots whose end
 *   time has already passed on today's date. The buyer booking flow leaves this false
 *   (you can't book a slot that has ended); the seller-facing dashboard calendar passes
 *   true so the day's earlier slots stay visible instead of disappearing as time passes.
 * @returns {Array} Array of time slot objects
 */
export const generateTimeSlotsForDate = (event, selectedDate, options = {}) => {
  // Defaults to false: callers (buyer flows) that omit it keep hiding already-ended slots.
  const { includePastSlots = false } = options;
  // Handle new flexible configuration
  if (event?.timeSlotConfig?.enabled) {
    const { timeSlotConfig } = event;
    const dateObj = parseISO(selectedDate);
    const dateStr = format(dateObj, 'yyyy-MM-dd');

    // Check for date-specific overrides
    const dateOverride = timeSlotConfig.dateOverrides?.find(override => override.date === dateStr);
    if (dateOverride) {
      if (!dateOverride.schedule) return []; // Closed on this date
      // Use override schedule
      const schedule = dateOverride.schedule;
      return generateSlotsFromSchedule(schedule, selectedDate, options);
    }

    // Get the schedule for this day of week
    const daySchedule = getDaySchedule(timeSlotConfig, dateObj);
    if (!daySchedule?.enabled) return [];

    return generateSlotsFromSchedule(daySchedule, selectedDate, options);
  }

  // Fallback to old configuration for backward compatibility
  if (!event?.scheduleConfig || event.scheduleConfig.type !== 'DAILY_RECURRING') {
    return [];
  }

  const { dailySchedule, exceptions = [] } = event.scheduleConfig;

  // Check if date has exceptions
  const exception = exceptions.find(ex => ex.date === selectedDate);
  if (exception?.type === 'CLOSED') return [];

  // Use exception config or default daily schedule
  const config = exception?.type === 'MODIFIED' ? exception : dailySchedule;

  // Generate hourly slots
  const slots = [];
  const startHour = parseInt(config.startTime.split(':')[0]);
  const endHour = parseInt(config.endTime.split(':')[0]);

  // Same past-slot rule as generateSlotsFromSchedule: hide slots that have already ended
  // today for the buyer flow (default), keep them when the seller calendar opts in.
  const now = new Date();
  const todayString = format(now, 'yyyy-MM-dd');
  const filterPast = !includePastSlots && selectedDate === todayString;
  const currentTimeMinutes = filterPast ? (now.getHours() * 60 + now.getMinutes()) : 0;

  for (let hour = startHour; hour < endHour; hour++) {
    // Skip slots whose end time (the next hour) has already passed.
    if (filterPast && (hour + 1) * 60 <= currentTimeMinutes) continue;

    const slotId = `${selectedDate}_${hour.toString().padStart(2, '0')}00`;
    const startTime = `${hour.toString().padStart(2, '0')}:00`;
    const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;

    slots.push({
      id: slotId,
      date: selectedDate,
      startTime: startTime,
      endTime: endTime,
      startDateTime: `${selectedDate}T${startTime}:00`,
      endDateTime: `${selectedDate}T${endTime}:00`,
      maxCapacity: config.maxCapacity || dailySchedule.maxCapacity,
      bookedCount: 0, // Will be updated from API
      isAvailable: true,
      availableSpots: config.maxCapacity || dailySchedule.maxCapacity
    });
  }

  // Ensure slots are sorted by start time (for RTL language layouts)
  return slots.sort((a, b) => {
    const aHour = parseInt(a.startTime.split(':')[0]);
    const bHour = parseInt(b.startTime.split(':')[0]);
    return aHour - bHour;
  });
};

/**
 * Generate slots from a schedule configuration
 * @param {Object} schedule - Schedule configuration with startTime, endTime, etc.
 * @param {string} selectedDate - Date in YYYY-MM-DD format
 * @returns {Array} Array of time slot objects
 */
const generateSlotsFromSchedule = (schedule, selectedDate, options = {}) => {
  const { includePastSlots = false } = options;
  const slots = [];
  const startMinutes = parseTimeToMinutes(schedule.startTime);
  const endMinutes = parseTimeToMinutes(schedule.endTime);
  const slotDuration = schedule.slotDuration || 60;
  const slotCapacity = schedule.slotCapacity || 20;
  const breakTimes = schedule.breakTimes || [];

  // Filter out already-ended slots only for today, and only when the caller wants it
  // (the buyer booking flow). The seller calendar passes includePastSlots so the day's
  // earlier slots stay on the calendar instead of vanishing as the clock advances.
  const now = new Date();
  const todayString = format(now, 'yyyy-MM-dd');
  const filterPast = !includePastSlots && selectedDate === todayString;
  const currentTimeMinutes = filterPast ? (now.getHours() * 60 + now.getMinutes()) : 0;
  let currentMinutes = startMinutes;

  while (currentMinutes < endMinutes) {
    const slotEndMinutes = currentMinutes + slotDuration;

    // Skip slots that have already ended (check end time, not start time)
    // This allows booking ongoing slots that haven't finished yet
    if (filterPast && slotEndMinutes <= currentTimeMinutes) {
      currentMinutes = slotEndMinutes;
      continue;
    }

    // Check if this slot overlaps with any break time
    const overlappingBreak = breakTimes.find(breakTime => {
      const breakStart = parseTimeToMinutes(breakTime.startTime);
      const breakEnd = parseTimeToMinutes(breakTime.endTime);
      return (currentMinutes >= breakStart && currentMinutes < breakEnd) ||
             (slotEndMinutes > breakStart && slotEndMinutes <= breakEnd) ||
             (currentMinutes <= breakStart && slotEndMinutes >= breakEnd);
    });

    if (overlappingBreak) {
      // Jump to end of break so next slot starts right after the break
      const breakEnd = parseTimeToMinutes(overlappingBreak.endTime);
      currentMinutes = breakEnd;
      continue;
    }

    if (slotEndMinutes <= endMinutes) {
      const startTime = formatMinutesToTime(currentMinutes);
      const endTime = formatMinutesToTime(slotEndMinutes);
      const slotId = `${selectedDate}_${startTime.replace(':', '')}`;

      slots.push({
        id: slotId,
        date: selectedDate,
        startTime: startTime,
        endTime: endTime,
        startDateTime: `${selectedDate}T${startTime}:00`,
        endDateTime: `${selectedDate}T${endTime}:00`,
        maxCapacity: slotCapacity,
        bookedCount: 0,
        isAvailable: true,
        availableSpots: slotCapacity,
        slotDuration
      });
    }

    currentMinutes = slotEndMinutes;
  }

  return slots;
};

/**
 * Check if a date is on a configured open day for this event.
 * Honors season range, dateOverrides, recurring exceptions, and daySchedule.enabled.
 * Does NOT exclude past dates or apply the buyer-facing advance booking window —
 * use this when rendering a seller-facing schedule view that should include history.
 *
 * @param {Object} event - Event object with scheduleConfig or timeSlotConfig
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {boolean}
 */
export const isDateScheduled = (event, date) => {
  const selectedDate = parseISO(date);

  if (event.start_date && isBefore(selectedDate, startOfDay(parseISO(event.start_date)))) {
    return false;
  }
  if (event.end_date && isAfter(selectedDate, startOfDay(parseISO(event.end_date)))) {
    return false;
  }

  if (event?.timeSlotConfig?.enabled) {
    const { timeSlotConfig } = event;

    const dateOverride = timeSlotConfig.dateOverrides?.find(override => override.date === date);
    if (dateOverride) {
      return dateOverride.schedule !== null;
    }

    if (timeSlotConfig.recurringExceptions) {
      for (const exception of timeSlotConfig.recurringExceptions) {
        if (exception.dayOfMonth === selectedDate.getDate() && exception.closed) {
          return false;
        }
      }
    }

    const daySchedule = getDaySchedule(timeSlotConfig, selectedDate);
    return daySchedule?.enabled === true;
  }

  if (!event?.scheduleConfig) return false;

  const { dailySchedule, exceptions = [] } = event.scheduleConfig;
  const exception = exceptions.find(ex => ex.date === date);

  if (exception?.type === 'CLOSED') return false;
  // MODIFIED exceptions must override operatingDays — generateTimeSlotsForDate honors
  // them regardless of weekday, so skip the operatingDays guard when one is present.
  if (exception?.type !== 'MODIFIED' && dailySchedule.operatingDays) {
    const dayOfWeek = selectedDate.getDay() || 7;
    if (!dailySchedule.operatingDays.includes(dayOfWeek)) return false;
  }

  return true;
};

/**
 * Check if a date is bookable by an end user. Layers past-date + advance-booking
 * checks on top of isDateScheduled.
 *
 * @param {Object} event - Event object with scheduleConfig or timeSlotConfig
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {boolean}
 */
export const isDateBookable = (event, date) => {
  const selectedDate = parseISO(date);
  const today = startOfDay(new Date());

  if (isBefore(selectedDate, today)) return false;
  if (!isDateScheduled(event, date)) return false;

  if (event?.timeSlotConfig?.enabled) {
    const bookingWindow = event.timeSlotConfig.bookingWindow || {};
    if (bookingWindow.advanceBookingDays != null) {
      const maxBookingDate = addDays(today, bookingWindow.advanceBookingDays);
      if (isAfter(selectedDate, maxBookingDate)) return false;
    }
  } else if (event?.scheduleConfig) {
    const bookingRules = event.scheduleConfig.bookingRules || {};
    if (bookingRules.advanceBookingDays != null) {
      const maxBookingDate = addDays(today, bookingRules.advanceBookingDays);
      if (isAfter(selectedDate, maxBookingDate)) return false;
    }
  }

  return true;
};

/**
 * Get the next available booking date
 * @param {Object} event - Event object with scheduleConfig or timeSlotConfig
 * @returns {string} Next available date in YYYY-MM-DD format
 */
export const getNextAvailableDate = (event) => {
  let checkDate = new Date();
  let maxDays = 30; // default

  // Get max days from appropriate config
  if (event?.timeSlotConfig?.enabled) {
    maxDays = event.timeSlotConfig.bookingWindow?.advanceBookingDays || 30;
  } else if (event?.scheduleConfig) {
    maxDays = event.scheduleConfig.bookingRules?.advanceBookingDays || 30;
  } else {
    return format(new Date(), 'yyyy-MM-dd');
  }

  for (let i = 0; i < maxDays; i++) {
    const dateString = format(checkDate, 'yyyy-MM-dd');
    if (isDateBookable(event, dateString)) {
      return dateString;
    }
    checkDate = addDays(checkDate, 1);
  }

  return format(new Date(), 'yyyy-MM-dd');
};

/**
 * Generate every bookable time slot across the event's booking window, flattened.
 * Wraps the single-date generateTimeSlotsForDate over each bookable date from today
 * forward through advanceBookingDays. Used to "spread" a time-slot event into one
 * card per slot on the seller page.
 *
 * @param {Object} event - Event object with timeSlotConfig (or legacy scheduleConfig)
 * @returns {Array} Flat array of slot objects (each as emitted by generateTimeSlotsForDate)
 */
export const generateTimeSlotsForRange = (event) => {
  if (!hasRecurringTimeSlots(event)) return [];

  // Gate on timeSlotConfig.enabled (mirrors getNextAvailableDate/isDateBookable) so a
  // partially-migrated event with a disabled timeSlotConfig falls through to scheduleConfig.
  const maxDays = event?.timeSlotConfig?.enabled
    ? (event.timeSlotConfig.bookingWindow?.advanceBookingDays ?? 30)
    : (event?.scheduleConfig?.bookingRules?.advanceBookingDays ?? 30);

  const slots = [];
  let checkDate = new Date();
  // isDateBookable enforces an inclusive window (today .. today+advanceBookingDays), so
  // iterate i <= maxDays to cover the final bookable day; out-of-window dates are filtered.
  for (let i = 0; i <= maxDays; i++) {
    const dateString = format(checkDate, 'yyyy-MM-dd');
    if (isDateBookable(event, dateString)) {
      slots.push(...generateTimeSlotsForDate(event, dateString));
    }
    checkDate = addDays(checkDate, 1);
  }
  return slots;
};

/**
 * Check if event has recurring time slots
 * @param {Object} event - Event object
 * @returns {boolean} Whether event has recurring time slots
 */
export const hasRecurringTimeSlots = (event) => {
  // Check for new flexible configuration
  if (event?.timeSlotConfig?.enabled) {
    return true;
  }
  // Fallback to old configuration for backward compatibility
  return event?.scheduleConfig?.type === 'DAILY_RECURRING';
};

/**
 * Update slot availability with booking data
 * @param {Array} slots - Generated time slots
 * @param {Array} bookings - Booking data from API (server format)
 * @returns {Array} Updated slots with availability
 */
export const updateSlotsWithBookings = (slots, bookings = []) => {
  return slots.map(slot => {
    // Find matching booking data from server response
    // Server returns format: { startTime: "0900", availableCount: 49, bookedCount: 1, ... }
    const serverSlot = bookings.find(booking => {
      // Handle both "0900" and "09:00" formats
      const serverTime = booking.startTime?.includes(':')
        ? booking.startTime.replace(':', '')
        : booking.startTime;
      const slotTime = slot.startTime?.includes(':')
        ? slot.startTime.replace(':', '')
        : slot.startTime;

      return serverTime === slotTime &&
        (!booking.date || booking.date === slot.date);
    });

    if (serverSlot) {
      // Step 1: determine the correct capacity limit.
      // slot.maxCapacity comes from generateTimeSlotsForDate() which reads dateOverrides → always correct.
      // serverSlot.totalCapacity comes from the availability endpoint which ignores dateOverrides → may be stale.
      // We take the lower of the two so a dateOverride that reduces capacity is always respected.
      // slotCapacity falls back to 0 if slot.maxCapacity is missing (misconfigured slot) to avoid NaN propagation.
      const slotCapacity = slot.maxCapacity ?? 0;
      const effectiveCapacity = Math.min(
        serverSlot.totalCapacity ?? slotCapacity, // fall back to slotCapacity if server sends null/undefined
        slotCapacity
      );

      // Step 2: compute how many spots the server considers "held" (booked + pending + any other holds).
      // We derive this from totalCapacity - availableCount rather than using bookedCount alone,
      // so any pending-order logic the server applies is preserved.
      // e.g. totalCapacity=120, availableCount=115 → heldSpots=5 (2 booked + 3 pending)
      let availableSpots;
      if (serverSlot.availableCount !== undefined && serverSlot.totalCapacity !== undefined) {
        // Clamp to >= 0: guards against server returning availableCount > totalCapacity (data race / server bug),
        // which would otherwise make heldSpots negative and inflate availableSpots above effectiveCapacity.
        const heldSpots = Math.max(0, serverSlot.totalCapacity - serverSlot.availableCount);
        // Step 3: apply held spots to the correct (possibly overridden) capacity.
        // e.g. effectiveCapacity=20, heldSpots=2 → availableSpots=18
        availableSpots = Math.max(0, effectiveCapacity - heldSpots);
      } else {
        availableSpots = Math.max(0, effectiveCapacity - (serverSlot.bookedCount || 0));
      }

      return {
        ...slot,
        bookedCount: serverSlot.bookedCount || 0,
        availableSpots,
        isAvailable: availableSpots > 0,
        totalCapacity: effectiveCapacity,
        pendingCount: serverSlot.pendingCount || 0,
        serverData: serverSlot
      };
    }

    // No booking data from server - slot is fully available
    return {
      ...slot,
      bookedCount: 0,
      availableSpots: slot.maxCapacity,
      isAvailable: true
    };
  });
};

/**
 * Format time slot for display
 * @param {Object} slot - Time slot object
 * @param {string} lang - Language code (optional)
 * @returns {string} Formatted time range
 */
export const formatTimeSlot = (slot, lang) => {
  // For Hebrew (RTL), show later time first
  if (lang === 'he') {
    return `${slot.endTime} - ${slot.startTime}`;
  }
  // For other languages (LTR), show earlier time first
  return `${slot.startTime} - ${slot.endTime}`;
};

/**
 * Format the booked time slot of an event order into a "date | time range" string,
 * e.g. "01/07/2026 | 09:00 - 10:00". Returns null when the order has no time slot.
 * @param {Object} eventOrder - Event order holding timeSlotId/timeSlotDate/timeSlotTime/timeSlotDuration
 * @param {string} lang - Language code (optional, 'he' wraps the time range with LRM marks)
 * @returns {string|null} Formatted date + time range, or null for non-timeslot orders
 */
export const formatAppointmentDateTime = (eventOrder, lang) => {
  const { timeSlotId, timeSlotDate, timeSlotTime, timeSlotDuration } = eventOrder || {};
  if (!timeSlotId) {
    return null;
  }
  try {
    const locale = lang === 'he' ? he : enUS;

    // Parse the date (prefer explicit timeSlotDate, fall back to the id's date part)
    let dateString = '';
    const dateSource = timeSlotDate || timeSlotId.split('_')[0];
    if (dateSource) {
      dateString = format(parseISO(dateSource), 'dd/MM/yyyy', { locale });
    }

    // Format the time range
    let timeString = '';
    if (timeSlotTime) {
      const startTime = timeSlotTime.length === 4
        ? `${timeSlotTime.substring(0, 2)}:${timeSlotTime.substring(2, 4)}`
        : timeSlotTime;

      if (timeSlotDuration) {
        const [startHour, startMin] = startTime.split(':').map(Number);
        const totalMinutes = startHour * 60 + startMin + timeSlotDuration;
        const endHour = Math.floor(totalMinutes / 60);
        const endMin = totalMinutes % 60;
        const endTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
        timeString = lang === 'he'
          ? `‎${startTime} - ${endTime}‎` // LRM wrapper for RTL
          : `${startTime} - ${endTime}`;
      } else {
        // No duration, show only start time
        timeString = startTime;
      }
    } else {
      // Fallback: extract from timeSlotId and assume 1-hour duration
      const [, timePart] = timeSlotId.split('_');
      if (timePart) {
        const startTime = `${timePart.substring(0, 2)}:${timePart.substring(2, 4)}`;
        const endHour = (parseInt(timePart.substring(0, 2), 10) + 1).toString().padStart(2, '0');
        const endTime = `${endHour}:${timePart.substring(2, 4)}`;
        timeString = lang === 'he'
          ? `‎${startTime} - ${endTime}‎` // LRM wrapper for RTL
          : `${startTime} - ${endTime}`;
      }
    }

    if (dateString && timeString) {
      return `${dateString} | ${timeString}`;
    }
  } catch (error) {
    console.error('Error formatting appointment time:', error);
  }
  return null;
};

// ============================================================================
// PURE HELPER FUNCTIONS
// ============================================================================

/**
 * Parse slot ID into its components
 * @pure
 */
const parseSlotId = (slotId) => {
  const [datePart, timePart] = slotId.split('_');
  return {
    datePart,
    hour: timePart.substring(0, 2),
    minute: timePart.substring(2, 4)
  };
};

/**
 * Find slot by ID in availableSlots array
 * @pure
 */
const findSlot = (slotId, slots) =>
  slots?.find(slot => slot.id === slotId) || null;

/**
 * Extract time slot data from slot object
 * @pure
 */
const extractTimeSlot = (slot) => ({
  startTime: slot.startTime,
  endTime: slot.endTime
});

/**
 * Get slot duration from event configuration
 * @pure
 */
const getSlotDuration = (event) => {
   return  event?.timeSlotConfig?.enabled
       ? event.timeSlotConfig.defaultSlotDuration || 60
       : event?.scheduleConfig?.dailySchedule?.slotDuration || 60;
}


/**
 * Calculate end time based on start time and duration
 * @pure
 */
const calculateEndTime = (hour, minute, duration) => {
  const totalMinutes = parseInt(hour) * 60 + parseInt(minute) + duration;
  const endHour = Math.floor(totalMinutes / 60);
  const endMin = totalMinutes % 60;
  return `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
};

/**
 * Build time slot object from start and end times
 * @pure
 */
const buildTimeSlot = (startTime, endTime) => ({
  startTime,
  endTime
});

/**
 * Get locale for date formatting
 * @pure
 */
const getLocale = (lang) => lang === 'he' ? he : enUS;

/**
 * Format date with locale
 * @pure
 */
const formatDate = (datePart, locale) =>
  format(parseISO(datePart), 'EEEE, dd MMMM yyyy', { locale });

/**
 * Format complete date-time string
 * @pure
 */
const formatDateTime = (datePart, timeSlot, currentLang) => {
  const locale = getLocale(currentLang);
  const dateString = formatDate(datePart, locale);
  const timeString = formatTimeSlot(timeSlot, currentLang);
  return `${dateString} | ${timeString}`;
};

// ============================================================================
// COMPOSED FUNCTIONS (Primary & Fallback Paths)
// ============================================================================

/**
 * Attempt to format slot from availableSlots array (primary path)
 * @pure (except for early return pattern)
 * @returns {string | null} Formatted string or null if slot not found
 */
const attemptPrimaryPath = ({ selectedTimeSlot, availableSlots, currentLang }) => {
  // Find slot (returns null if not found)
  const slot = findSlot(selectedTimeSlot, availableSlots);

  // Early return if not found
  if (!slot) return null;

  // Transform and format
  const timeSlot = extractTimeSlot(slot);
  const { datePart } = parseSlotId(selectedTimeSlot);

  return formatDateTime(datePart, timeSlot, currentLang);
};

/**
 * Format slot using fallback calculation
 * @pure (except console.warn)
 * @returns {string} Formatted string
 */
const attemptFallbackPath = ({ selectedTimeSlot, event, currentLang }) => {
  console.warn('[timeSlots] Selected slot not found in availableSlots, using fallback calculation');

  const { datePart, hour, minute } = parseSlotId(selectedTimeSlot);
  const duration = getSlotDuration(event);
  const startTime = `${hour}:${minute}`;
  const endTime = calculateEndTime(hour, minute, duration);
  const timeSlot = buildTimeSlot(startTime, endTime);

  return formatDateTime(datePart, timeSlot, currentLang);
};

/**
 * Format selected time slot with date for display in checkout
 *
 * Uses pragmatic functional programming:
 * - Pure helper functions for all operations
 * - Early returns for null handling (simple and clear)
 * - Function composition for control flow
 * - Immutable data transformations
 *
 * @param {Object} options - Configuration options
 * @param {string} options.selectedTimeSlot - Slot ID (e.g., "2025-09-23_1300")
 * @param {string} options.selectedDate - Date string in YYYY-MM-DD format
 * @param {Array} options.availableSlots - Array of slot objects with startTime/endTime
 * @param {string} options.currentLang - Language code ('he' for Hebrew, otherwise LTR)
 * @param {Object} options.event - Event object (for fallback calculations)
 * @returns {string|null} Formatted date and time string, or null if invalid
 *
 * @example
 * formatSelectedSlotDateTime({
 *   selectedTimeSlot: "2025-09-23_1300",
 *   selectedDate: "2025-09-23",
 *   availableSlots: [...],
 *   currentLang: "en",
 *   event: {...}
 * })
 * // => "Tuesday, 23 September 2025 | 13:00 - 14:00"
 */
export const formatSelectedSlotDateTime = (options) => {
  const { selectedDate, selectedTimeSlot } = options;

  // Guard clause: early return for invalid input
  if (!selectedDate || !selectedTimeSlot) return null;

  try {
    // Try primary path (availableSlots lookup)
    const primaryResult = attemptPrimaryPath(options);

    // If found in availableSlots, return it
    if (primaryResult) return primaryResult;

    // Otherwise, use fallback calculation
    return attemptFallbackPath(options);

  } catch (err) {
    console.error('[timeSlots] Error formatting date/time:', err);
    // Ultimate fallback: return raw values
    return `${selectedDate} - ${selectedTimeSlot}`;
  }
};

/**
 * Get availability status for display
 * @param {Object} slot - Time slot object
 * @param {Function} translate - Translation function (optional)
 * @returns {Object} Status object with color, text, and translation key
 */
export const getAvailabilityStatus = (slot, translate) => {
  const { availableSpots, maxCapacity } = slot;
  const percentage = (availableSpots / maxCapacity) * 100;

  if (availableSpots === 0) {
    const text = translate ? translate('time_slots.fully_booked') : 'Fully Booked';
    return { color: 'error', text, variant: 'error', key: 'fully_booked' };
  } else if (percentage <= 20) {
    const text = translate ? translate('time_slots.only_x_left', { count: availableSpots }) : `Only ${availableSpots} left`;
    return { color: 'warning', text, variant: 'warning', key: 'limited_spots' };
  } else if (percentage <= 50) {
    const text = translate ? translate('time_slots.x_spots_left', { count: availableSpots }) : `${availableSpots} spots left`;
    return { color: 'info', text, variant: 'info', key: 'spots_available' };
  } else {
    const text = translate ? translate('time_slots.x_available', { count: availableSpots }) : `${availableSpots} available`;
    return { color: 'success', text, variant: 'success', key: 'many_available' };
  }
};

/**
 * Normalize a spreadsheet date cell to "YYYY-MM-DD".
 * Accepts "dd/mm/yyyy", "d/m/yyyy" and "yyyy-mm-dd". Returns null if unparseable.
 */
export const normalizeDateInput = (input) => {
  const s = String(input ?? '').trim();
  if (!s) return null;
  let y; let m; let d;
  let match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) { [, y, m, d] = match; }
  else {
    match = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    [, d, m, y] = match;
  }
  const mm = Number(m); const dd = Number(d);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const iso = `${y}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
  // Reject impossible calendar dates (e.g. 2026-02-31). parseISO returns an
  // Invalid Date for these; left unchecked they throw later in date-fns format().
  const parsed = parseISO(iso);
  if (!isValid(parsed) || format(parsed, 'yyyy-MM-dd') !== iso) return null;
  return iso;
};

/**
 * Normalize a spreadsheet time cell to "HHMM". Accepts "HH:MM", "H:MM", "HHMM".
 * Returns null if out of range / unparseable.
 */
export const normalizeTimeInput = (input) => {
  const s = String(input ?? '').trim();
  if (!s) return null;
  let h; let min;
  const colon = s.match(/^(\d{1,2}):(\d{2})$/);
  if (colon) { [, h, min] = colon; }
  else {
    const compact = s.match(/^(\d{2})(\d{2})$/);
    if (!compact) return null;
    [, h, min] = compact;
  }
  const hh = Number(h); const mm = Number(min);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return `${String(hh).padStart(2, '0')}${String(mm).padStart(2, '0')}`;
};

/**
 * Resolve a spreadsheet Date+Time pair to one of the event's real time slots.
 * Returns the slot object ({ id, date, startTime, endTime, slotDuration, ... }) or null.
 */
export const resolveTimeSlotForInput = (event, dateInput, timeInput) => {
  const isoDate = normalizeDateInput(dateInput);
  const hhmm = normalizeTimeInput(timeInput);
  if (!isoDate || !hhmm) return null;
  const wanted = `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`; // "0900" -> "09:00"
  const slots = generateTimeSlotsForDate(event, isoDate);
  return slots.find((slot) => slot.startTime === wanted) || null;
};
